// src/components/owners/OwnerExportModal.jsx
import React, { useState } from 'react';
import axios from 'axios';
import { X, Download, FileText, FileSpreadsheet, Loader, CheckCircle, AlertCircle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const OwnerExportModal = ({ selectedOwners, isOpen, onClose }) => {
  const [format, setFormat] = useState('pdf');
  const [includeNotes, setIncludeNotes] = useState(true);
  const [includeContacts, setIncludeContacts] = useState(true);
  const [includeAircraft, setIncludeAircraft] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [jobStatus, setJobStatus] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);

  const handleExport = async () => {
    if (selectedOwners.length === 0) {
      alert('No clients selected');
      return;
    }

    setExporting(true);
    setJobStatus({ status: 'pending', message: 'Creating export job...' });

    try {
      // Créer le job d'export
      const response = await axios.post(
        `${API_BASE}/owners/export`,
        {
          owner_ids: selectedOwners.map(o => o.id),
          format: format,
          include_notes: includeNotes,
          include_contacts: includeContacts,
          include_aircraft: includeAircraft
        }
        // Pas besoin de headers, axios les ajoute automatiquement
      );

      const jobId = response.data.job_id;
      setJobStatus({ status: 'processing', message: 'Processing export...', jobId });

      // Poll pour le statut
      const interval = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API_BASE}/owners/export/${jobId}/status`);
          
          if (statusRes.data.status === 'completed') {
            clearInterval(interval);
            setPollingInterval(null);
            setJobStatus({ 
              status: 'completed', 
              message: 'Export ready! Downloading...', 
              jobId
            });
            
            // Télécharger le fichier avec axios
            const downloadRes = await axios.get(`${API_BASE}/owners/export/${jobId}/download`, {
              responseType: 'blob'
            });
            
            // Créer un lien de téléchargement
            const url = window.URL.createObjectURL(new Blob([downloadRes.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `clients_export_${new Date().toISOString().slice(0,19).replace(/:/g, '-')}.${format}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            setTimeout(() => {
              onClose();
              resetState();
            }, 2000);
            
          } else if (statusRes.data.status === 'failed') {
            clearInterval(interval);
            setPollingInterval(null);
            setJobStatus({ 
              status: 'failed', 
              message: statusRes.data.error_message || 'Export failed'
            });
          }
        } catch (err) {
          console.error('Error polling status:', err);
        }
      }, 2000);
      
      setPollingInterval(interval);
      
    } catch (err) {
      console.error('Export error:', err);
      setJobStatus({ 
        status: 'failed', 
        message: err.response?.data?.detail || 'Export failed'
      });
    }
  };

  const resetState = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }
    setExporting(false);
    setJobStatus(null);
    setFormat('pdf');
    setIncludeNotes(true);
    setIncludeContacts(true);
    setIncludeAircraft(true);
  };

  const handleClose = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }
    resetState();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-xl w-full max-w-md m-4">
        <div className="p-4 border-b border-gray-700 flex justify-between items-center">
          <h3 className="text-lg font-bold text-white">Export Clients</h3>
          <button onClick={handleClose} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
            <p className="text-sm text-gray-300">
              Exporting <span className="text-blue-400 font-medium">{selectedOwners.length}</span> client(s)
            </p>
          </div>

          <div>
            <label className="block text-gray-400 text-sm mb-2">Format</label>
            <div className="flex gap-3">
              <button
                onClick={() => setFormat('pdf')}
                className={`flex-1 py-2 rounded-lg border flex items-center justify-center gap-2 transition ${format === 'pdf' ? 'bg-blue-600 border-blue-500 text-white' : 'bg-gray-700 border-gray-600 text-gray-400 hover:text-white'}`}
              >
                <FileText size={16} /> PDF
              </button>
              <button
                onClick={() => setFormat('xlsx')}
                className={`flex-1 py-2 rounded-lg border flex items-center justify-center gap-2 transition ${format === 'xlsx' ? 'bg-blue-600 border-blue-500 text-white' : 'bg-gray-700 border-gray-600 text-gray-400 hover:text-white'}`}
              >
                <FileSpreadsheet size={16} /> Excel
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeNotes}
                onChange={(e) => setIncludeNotes(e.target.checked)}
                className="w-4 h-4 accent-blue-500"
              />
              <span className="text-white text-sm">Include notes history</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeContacts}
                onChange={(e) => setIncludeContacts(e.target.checked)}
                className="w-4 h-4 accent-blue-500"
              />
              <span className="text-white text-sm">Include contacts</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeAircraft}
                onChange={(e) => setIncludeAircraft(e.target.checked)}
                className="w-4 h-4 accent-blue-500"
              />
              <span className="text-white text-sm">Include aircraft</span>
            </label>
          </div>

          {jobStatus && (
            <div className={`p-3 rounded-lg ${jobStatus.status === 'completed' ? 'bg-green-500/20 border border-green-500/30' : jobStatus.status === 'failed' ? 'bg-red-500/20 border border-red-500/30' : 'bg-gray-700/50'}`}>
              <div className="flex items-center gap-2">
                {jobStatus.status === 'processing' && <Loader size={16} className="animate-spin text-blue-400" />}
                {jobStatus.status === 'completed' && <CheckCircle size={16} className="text-green-400" />}
                {jobStatus.status === 'failed' && <AlertCircle size={16} className="text-red-400" />}
                <span className="text-sm text-gray-300">{jobStatus.message}</span>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-700 flex gap-3">
          <button onClick={handleClose} className="flex-1 px-4 py-2 bg-gray-700 rounded-lg text-sm">
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={exporting || selectedOwners.length === 0}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {exporting ? (
              <Loader size={16} className="animate-spin" />
            ) : (
              <Download size={16} />
            )}
            Export
          </button>
        </div>
      </div>
    </div>
  );
};

export default OwnerExportModal;